// src/pages/finance-portal/connected-accounts/ConnectedAccounts.js
// Full management page for connected bank accounts

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    Grid,
    Button,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    Skeleton,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import StripeLinkButton from '../components/StripeLinkButton';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const ACCOUNT_TYPE_ICONS = {
    DEPOSITORY: <SavingsIcon />,
    CREDIT: <CreditCardIcon />,
    LOAN: <ReceiptLongIcon />,
    INVESTMENT: <TrendingUpIcon />,
    OTHER: <AccountBalanceWalletIcon />,
};

const ACCOUNT_TYPE_COLORS = {
    DEPOSITORY: '#22c55e',
    CREDIT: '#f59e0b',
    LOAN: '#ef4444',
    INVESTMENT: '#8b5cf6',
    OTHER: '#64748b',
};

const STATUS_CONFIG = {
    ACTIVE: { color: '#22c55e', icon: <CheckCircleIcon sx={{ fontSize: 14 }} />, label: 'Connected' },
    ERROR: { color: '#ef4444', icon: <ErrorIcon sx={{ fontSize: 14 }} />, label: 'Error' },
    INACTIVE: { color: '#94a3b8', icon: <ErrorIcon sx={{ fontSize: 14 }} />, label: 'Inactive' },
    REVOKED: { color: '#ef4444', icon: <ErrorIcon sx={{ fontSize: 14 }} />, label: 'Revoked' },
};

function formatCurrency(amount, currency = 'USD') {
    if (amount == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
    }).format(amount);
}

export default function ConnectedAccounts() {
    const [accounts, setAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshingId, setRefreshingId] = useState(null);
    const [disconnectDialog, setDisconnectDialog] = useState({ open: false, itemId: null, name: '' });
    const [disconnecting, setDisconnecting] = useState(false);
    const [connecting, setConnecting] = useState(false);

    const fetchAccounts = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get('/api/finance/stripe/accounts');
            if (response.data.success) {
                setAccounts(response.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load accounts');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    const handleRefreshBalance = async (accountId) => {
        setRefreshingId(accountId);
        try {
            const response = await axiosInstance.get(`/api/finance/stripe/accounts/${accountId}/balance`);
            if (response.data.success) {
                setAccounts(prev => prev.map(a => a.id === accountId ? response.data.data : a));
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to refresh balance');
        } finally {
            setRefreshingId(null);
        }
    };

    const handleDisconnect = async () => {
        setDisconnecting(true);
        try {
            await axiosInstance.delete(`/api/finance/stripe/connections/${disconnectDialog.itemId}`);
            setDisconnectDialog({ open: false, itemId: null, name: '' });
            await fetchAccounts();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to disconnect bank');
        } finally {
            setDisconnecting(false);
        }
    };

    const handleAddBankSuccess = async (sessionId, metadata) => {
        setConnecting(true);
        setError(null);
        try {
            await axiosInstance.post('/api/finance/stripe/complete-connection', {
                session_id: sessionId,
            });
            await fetchAccounts();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to connect bank');
        } finally {
            setConnecting(false);
        }
    };

    // Group by institution
    const accountsByInstitution = accounts.reduce((groups, account) => {
        const key = account.institution_name || 'Unknown Bank';
        if (!groups[key]) {
            groups[key] = { accounts: [], itemStatus: account.connection_status };
        }
        groups[key].accounts.push(account);
        return groups;
    }, {});

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AccountBalanceIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Connected Accounts
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                Manage your bank connections
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>
                        {error}
                    </Alert>
                )}

                {/* Add Bank Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
                    <StripeLinkButton
                        onSuccess={handleAddBankSuccess}
                        buttonText="Connect New Bank"
                        variant="contained"
                        disabled={connecting}
                    />
                </Box>

                {/* Connecting overlay */}
                {connecting && (
                    <Card sx={{
                        borderRadius: '16px',
                        border: `2px solid ${ACCENT}`,
                        boxShadow: 'none',
                        textAlign: 'center',
                        py: 5,
                        mb: 2.5,
                        bgcolor: 'rgba(34, 197, 94, 0.04)',
                    }}>
                        <CardContent>
                            <CircularProgress sx={{ color: ACCENT, mb: 2 }} />
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1.05rem', color: '#1e293b', mb: 0.5 }}>
                                Connecting your bank account...
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                Fetching account details and balances. This may take a few seconds.
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                {/* Loading */}
                {isLoading && !connecting && (
                    <Grid container spacing={2.5}>
                        {[1, 2, 3].map(i => (
                            <Grid item xs={12} md={4} key={i}>
                                <Skeleton variant="rounded" height={180} sx={{ borderRadius: '16px' }} />
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* No accounts */}
                {!isLoading && accounts.length === 0 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', textAlign: 'center', py: 6 }}>
                        <CardContent>
                            <AccountBalanceIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#64748b', mb: 0.5 }}>
                                No bank accounts connected
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                                Connect a bank account to get started with the finance portal
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                {/* Institution Groups */}
                {!isLoading && Object.entries(accountsByInstitution).map(([institutionName, group]) => {
                    const statusConfig = STATUS_CONFIG[group.itemStatus] || STATUS_CONFIG.ACTIVE;

                    return (
                        <Box key={institutionName} sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountBalanceIcon sx={{ fontSize: 20, color: '#64748b' }} />
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
                                        {institutionName}
                                    </Typography>
                                    <Chip
                                        icon={statusConfig.icon}
                                        label={statusConfig.label}
                                        size="small"
                                        sx={{
                                            fontFamily,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            height: 24,
                                            bgcolor: `${statusConfig.color}15`,
                                            color: statusConfig.color,
                                            '& .MuiChip-icon': { color: statusConfig.color },
                                        }}
                                    />
                                </Box>
                                <Tooltip title="Disconnect bank">
                                    <IconButton
                                        size="small"
                                        onClick={() => setDisconnectDialog({
                                            open: true,
                                            itemId: group.accounts[0]?.stripe_connection_id,
                                            name: institutionName,
                                        })}
                                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Grid container spacing={2}>
                                {group.accounts.map((account) => {
                                    const typeColor = ACCOUNT_TYPE_COLORS[account.type] || '#64748b';
                                    const typeIcon = ACCOUNT_TYPE_ICONS[account.type] || ACCOUNT_TYPE_ICONS.OTHER;

                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={account.id}>
                                            <Card sx={{
                                                borderRadius: '16px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow: 'none',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    borderColor: '#cbd5e1',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                                },
                                            }}>
                                                <CardContent sx={{ p: 2.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                                                        <Box sx={{
                                                            width: 44, height: 44, borderRadius: '12px',
                                                            bgcolor: `${typeColor}15`,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            color: typeColor,
                                                        }}>
                                                            {typeIcon}
                                                        </Box>
                                                        <Tooltip title="Refresh balance">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRefreshBalance(account.id)}
                                                                disabled={refreshingId === account.id}
                                                                sx={{ color: '#94a3b8', '&:hover': { color: ACCENT } }}
                                                            >
                                                                {refreshingId === account.id ? (
                                                                    <CircularProgress size={16} sx={{ color: ACCENT }} />
                                                                ) : (
                                                                    <RefreshIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>

                                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', color: '#1e293b', mb: 0.25 }}>
                                                        {account.name}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mb: 1.5 }}>
                                                        {account.subtype || account.type} {account.mask ? `****${account.mask}` : ''}
                                                    </Typography>

                                                    <Typography sx={{
                                                        fontFamily, fontWeight: 800, fontSize: '1.4rem',
                                                        color: (account.current_balance || 0) >= 0 ? '#1e293b' : '#ef4444',
                                                    }}>
                                                        {formatCurrency(account.current_balance, account.iso_currency_code)}
                                                    </Typography>
                                                    {account.available_balance != null && account.available_balance !== account.current_balance && (
                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mt: 0.25 }}>
                                                            Available: {formatCurrency(account.available_balance, account.iso_currency_code)}
                                                        </Typography>
                                                    )}

                                                    {!account.is_selected && (
                                                        <Chip
                                                            label="Not tracked"
                                                            size="small"
                                                            sx={{ fontFamily, fontSize: '0.7rem', mt: 1, bgcolor: '#fef3c7', color: '#92400e' }}
                                                        />
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    );
                })}
            </Box>

            {/* Disconnect Dialog */}
            <Dialog
                open={disconnectDialog.open}
                onClose={() => setDisconnectDialog({ open: false, itemId: null, name: '' })}
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Disconnect {disconnectDialog.name}?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>
                        This will remove the bank connection and all associated accounts. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDisconnectDialog({ open: false, itemId: null, name: '' })}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDisconnect}
                        disabled={disconnecting}
                        startIcon={disconnecting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' },
                        }}
                    >
                        {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
